import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtDate, monthName } from "../lib/dates";
import { X, Calendar, Package, CheckCircle2, AlertCircle, Clock, Star } from "lucide-react";
import { StarsInline } from "./StarRating";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function photoSrc(photo) {
  if (!photo) return null;
  if (photo.startsWith("http")) return photo;
  if (photo.startsWith("/api")) return `${BACKEND_URL}${photo}`;
  return `${BACKEND_URL}/api/files/${photo}`;
}

function Badge({ result }) {
  if (!result) return null;
  if (result.on_time) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
        <CheckCircle2 className="h-3 w-3" />
        {result.days_early > 0 ? `${result.days_early}d early` : "on deadline"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 bg-red-50 rounded-full px-2 py-0.5">
      <AlertCircle className="h-3 w-3" /> {result.days_breached}d late
    </span>
  );
}

/**
 * KPI detail drawer / modal. Shows the exact jobs that make up a KPI.
 * props:
 *  - open, onClose
 *  - kind: "created" | "completed" | "received" | "ontime" | "breached" | "rated"
 *  - year, month
 *  - manager_id (optional, for Owner-side manager scorecard KPIs)
 *  - title: string
 *  - metric: "count" | "qty" | "received" | "breach_days" | "rating"
 *            which number should be summed/shown at the top
 *  - metricLabel: label for the top total
 *  - testid
 */
export function KpiDetailModal({ open, onClose, kind, year, month, manager_id,
                                 title, metric = "count", metricLabel, testid }) {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    if (!open) return;
    setJobs(null);
    const params = { year, month, kind };
    if (manager_id) params.manager_id = manager_id;
    api.get("/reports/list", { params })
      .then((r) => setJobs(r.data || []))
      .catch(() => setJobs([]));
  }, [open, year, month, kind, manager_id]);

  if (!open) return null;

  const total = (() => {
    if (!jobs) return "…";
    if (metric === "count") return jobs.length.toLocaleString();
    if (metric === "qty") return jobs.reduce((s, j) => s + (j.quantity || 0), 0).toLocaleString();
    if (metric === "received") return jobs.reduce((s, j) => s + (j.received_quantity || j.quantity || 0), 0).toLocaleString();
    if (metric === "breach_days") {
      const days = jobs.map((j) => j.result?.days_breached || 0).filter((n) => n > 0);
      if (!days.length) return 0;
      return title.toLowerCase().includes("max")
        ? Math.max(...days)
        : Math.round((days.reduce((a, b) => a + b, 0) / days.length) * 10) / 10;
    }
    if (metric === "rating") {
      const rs = jobs.map((j) => j.quality_rating).filter(Boolean);
      if (!rs.length) return "—";
      return (Math.round((rs.reduce((a, b) => a + b, 0) / rs.length) * 10) / 10) + " / 5";
    }
    return jobs.length;
  })();

  return (
    <div data-testid={testid || "kpi-detail-modal"}
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
      <div className="w-full sm:max-w-xl bg-white rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{monthName(month)} {year}</p>
            <h3 className="font-display text-lg font-black text-slate-900 truncate" data-testid="kpi-modal-title">{title}</h3>
            <p className="text-sm text-slate-500 mt-0.5">
              <b className="tnum text-slate-800" data-testid="kpi-modal-total">{total}</b>
              <span className="ml-1 text-slate-500">{metricLabel || "jobs"}</span>
              {jobs && <span className="text-slate-400"> · {jobs.length} job{jobs.length === 1 ? "" : "s"}</span>}
            </p>
          </div>
          <button onClick={onClose} data-testid="kpi-modal-close"
            className="p-2 -m-2 text-slate-400 active:text-[#9A462B]"><X className="h-5 w-5" /></button>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3" data-testid="kpi-modal-list">
          {jobs === null ? (
            <p className="text-sm text-slate-400 text-center py-6">Loading…</p>
          ) : jobs.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No jobs contribute to this KPI for the selected month.</p>
          ) : (
            jobs.map((j) => {
              const img = photoSrc(j.photo);
              return (
                <div key={j.id} data-testid={`kpi-job-row-${j.id}`}
                  className="flex gap-3 rounded-xl border border-slate-200 p-3">
                  <div className="h-16 w-16 flex-shrink-0 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center">
                    {img ? (
                      <img src={img} alt={j.product_name} className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <Package className="h-6 w-6 text-slate-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="tnum font-bold text-slate-900 text-sm">{j.job_number}</span>
                      <Badge result={j.result} />
                    </div>
                    <p className="text-sm text-slate-800 font-semibold truncate">{j.product_name}</p>
                    <p className="text-[11px] text-slate-500">{j.product_type || "—"}</p>
                    <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-500">
                      <span>Qty <b className="tnum text-slate-800">{j.quantity}</b></span>
                      {j.received_quantity != null && (
                        <span>Received <b className="tnum text-emerald-700">{j.received_quantity}</b></span>
                      )}
                      {j.quality_rating ? (
                        <span className="inline-flex items-center gap-0.5">
                          <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                          <b className="tnum text-amber-700">{j.quality_rating}/5</b>
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-slate-400">
                      <span className="inline-flex items-center gap-0.5"><Calendar className="h-3 w-3" /> Deadline {fmtDate(j.deadline)}</span>
                      {j.completion_date && (
                        <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" /> Done {fmtDate(j.completion_date)}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100">
          <button onClick={onClose} data-testid="kpi-modal-close-footer"
            className="w-full py-3 rounded-xl bg-slate-100 text-slate-700 font-bold active:scale-[0.98] transition-transform">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** Clickable KPI card — small helper used across dashboards. */
export function KpiCard({ value, label, color = "text-slate-900", onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="w-full rounded-xl bg-transparent text-center transition-transform active:scale-[0.97] cursor-pointer group focus:outline-none focus:ring-2 focus:ring-[#C86D51]/40">
      <p className={`tnum text-2xl sm:text-3xl font-black ${color} group-hover:underline underline-offset-4 decoration-dotted`}>{value}</p>
      <p className="text-[10px] font-bold uppercase text-slate-400 mt-0.5">{label}</p>
    </button>
  );
}

export default KpiDetailModal;
